import { useEffect, useState, useCallback } from 'react'
import { documentService } from '@/services'
import { useAuth } from '@/hooks/useAuth'
import { NodeListPage } from '@/components/nodes/NodeListPage'
import { documentsListConfig } from '@/lib/config/node-list-configs'
import type { Document } from '@/types'
import type { DocumentFilters } from '@/components/documents'
import type { DocumentGroupByOption } from '@/lib/config/node-grouping-configs'
import { LoadingState, PageContainer } from '@/components/layout'

export default function DocumentsPage() {
  const { user } = useAuth()
  const [documents, setDocuments] = useState<Document[]>([])
  const [loading, setLoading] = useState(true)

  // NodeListPage state
  const [searchQuery, setSearchQuery] = useState('')
  const [activeTab, setActiveTab] = useState('all-documents')
  const [showFilters, setShowFilters] = useState(false)
  const [groupBy, setGroupBy] = useState<DocumentGroupByOption>('none')
  const [filters, setFilters] = useState<DocumentFilters>({ type: 'all' })

  const loadDocuments = useCallback(async () => {
    if (!user) return

    try {
      setLoading(true)
      const allDocuments = await documentService.getAll()

      // SuperAdmins see all documents, others see only documents they have access to
      if (user.role === 'SuperAdmin') {
        setDocuments(allDocuments)
      } else {
        // Filter documents where user is creator or has been shared with
        const userDocuments = allDocuments.filter(
          (doc) =>
            doc.creator.id === user.id ||
            doc.users.some((u) => u.id === user.id) ||
            doc.workspace.creator.id === user.id ||
            doc.workspace.users.some((u) => u.id === user.id)
        )
        setDocuments(userDocuments)
      }
    } catch (error) {
      console.error('Failed to load documents:', error)
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    loadDocuments()
  }, [loadDocuments])

  const handleFiltersChange = (newFilters: Partial<DocumentFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }) as DocumentFilters)
  }

  if (loading) {
    return (
      <PageContainer maxWidth="full">
        <LoadingState variant="spinner" text="Loading documents..." />
      </PageContainer>
    )
  }

  return (
    <NodeListPage
      nodes={documents}
      loading={loading}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      showFilters={showFilters}
      onFiltersToggle={() => setShowFilters(!showFilters)}
      filters={filters}
      onFiltersChange={handleFiltersChange}
      groupBy={groupBy}
      onGroupByChange={setGroupBy}
      config={{
        ...documentsListConfig,
        filterNodes: (
          nodes: Document[],
          tab: string | undefined,
          filterState: DocumentFilters,
          query: string
        ) =>
          documentsListConfig.filterNodes(
            nodes,
            tab,
            filterState,
            query,
            user ? { id: user.id, role: user.role } : undefined
          ),
      }}
      emptyStateDescription={
        searchQuery || filters.type !== 'all'
          ? 'Try adjusting your search criteria or filters'
          : 'Get started by uploading your first document'
      }
    />
  )
}
